import { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import type { FinancialCapabilities } from '@pokerroom/types';
import { api } from '../../../lib/api';
import { useAuthStore } from '../../../store/auth.store';
import { useCurrentVenue } from '../../../hooks/useCurrentVenue';

export function useFinancialCapabilities(memberId: string) {
  const user = useAuthStore((s) => s.user),
    actions = useAuthStore((s) => s.allowedActions);
  const revision = useAuthStore((s) => s.authRevision);
  const { venue } = useCurrentVenue();
  const can = (action: string) => !!user?.isSystemUser || !!actions?.includes(action);
  const query = useQuery({
    queryKey: ['member-financial-capabilities', user?.id, venue?.id, memberId, revision],
    queryFn: () => api.get<FinancialCapabilities>(`/members/${memberId}/financial-capabilities`),
    enabled:
      !!venue &&
      (can('member:adjust_points') ||
        can('member:charge_read') ||
        can('member:charge_confirm') ||
        can('member:zipa_transfer')),
    retry: false,
  });
  return {
    ...query,
    venue: venue as { id: string; name: string; code: string } | undefined,
    user,
    can,
    revision,
    identity: `${user?.id}:${venue?.id}:${memberId}`,
  };
}
/** Persist the exact uncertain request. Authorization failures cannot prove it did not commit. */
export function useFinancialOperation<Input, Preview, Receipt>(
  memberId: string,
  endpoint: string,
  identity: string,
) {
  const key = `member-financial:${identity}:${endpoint}`;
  type Pending = Input & { operationKey: string; quoteHash: string };
  const [pending, setPending] = useState<Pending | null>(() => {
    try {
      const p = JSON.parse(sessionStorage.getItem(key) ?? 'null');
      return p && typeof p.operationKey === 'string' && typeof p.quoteHash === 'string' ? p : null;
    } catch {
      return null;
    }
  });
  const [preview, setPreview] = useState<Preview | null>(null),
    [receipt, setReceipt] = useState<Receipt | null>(null);
  const [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  const inFlight = useRef(false),
    generation = useRef(0);
  const client = useQueryClient();
  const reset = () => {
    generation.current++;
    setPreview(null);
    setReceipt(null);
    setError('');
  };
  async function load(input: Input) {
    if (inFlight.current || pending) return;
    inFlight.current = true;
    setBusy(true);
    reset();
    const current = generation.current;
    try {
      const result = await api.post<Preview>(`/members/${memberId}/${endpoint}/preview`, input);
      if (current === generation.current) setPreview(result);
    } catch {
      setError('financial.previewFailed');
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }
  async function submit(body: Pending) {
    if (inFlight.current) return;
    try {
      sessionStorage.setItem(key, JSON.stringify(body));
    } catch {
      setError('financial.storageFailed');
      return;
    }
    inFlight.current = true;
    setPending(body);
    setBusy(true);
    setError('');
    try {
      const result = await api.post<Receipt>(`/members/${memberId}/${endpoint}/confirm`, body);
      sessionStorage.removeItem(key);
      setPending(null);
      setPreview(null);
      setReceipt(result);
      for (const queryKey of [
        ['member-zipa'],
        ['member-ledger-summary'],
        ['member-packages'],
        ['member-plans'],
        ['member-charges'],
        ['member-transfers'],
        ['zipa-daily-totals'],
        ['accounting-summary'],
      ])
        void client.invalidateQueries({ queryKey });
    } catch (failure) {
      const status = isAxiosError(failure) ? failure.response?.status : undefined;
      if (status && [400, 409, 422].includes(status)) {
        sessionStorage.removeItem(key);
        setPending(null);
        setPreview(null);
        setError('financial.stale');
      } else setError(status === 403 ? 'financial.permissionRecovery' : 'financial.uncertain');
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }
  return { pending, preview, receipt, busy, error, locked: busy || !!pending, reset, load, submit };
}
